import {
  DEFAULT_LOADABLE_STEP_KG,
  DEFAULT_LOADABLE_STEP_LB,
  type PlateLoadMode,
} from "./plateCalculator.js";
import { fromCanonicalKg, toCanonicalKg } from "./units.js";

// Floor a weight to the finest increment the athlete can actually load,
// in the row's display unit (LG-15). A %1RM prescription resolves to
// numbers like 68.38 kg that no plate set can build; flooring ("round
// DOWN") snaps to a loadable weight without ever prescribing MORE than
// the plates allow — erring light, never heavy.
//
// **Scope:** display + seed ONLY. Never applied to the saved prescription
// target (which stays the exact `targetWeight.percentage`); the caller
// runs this on the seeded logger cell, so the athlete sees a usable number
// while the plan's math is untouched.
//
// Unit-aware because the athlete reads the machine in the unit they picked per
// row: a kg row floors to the kg step, a lb row to the lb step. Returns
// canonical kg, so the caller formats it back through fromCanonicalKg like any
// stored weight.
//
// ⚠️ The two steps are NOT one step converted, and that is the whole point
// (LG-37b E1, `equipment-model.md` «L'invariante che sopravvive a tutto»): a pound rack's finest rung is
// 5 lb because it racks 2.5 lb plates, not because 2.5 kg converts to 5.51.
//
// Both steps are athlete-derived. The config layer's `loadRoundingContext` /
// `useRoundingContextRef` (`plateConfigCore.ts`) build the whole
// `LoadableContext` in one place, from each unit's own configured inventory
// (`roundingStepFor`) — never one step converted into the other. The three
// callers that floor a %1RM-derived seed (`planStarterHydration.ts:240`,
// `useReplaceExercise.ts:236`, `useDraftPersistence.ts:155`) all pass that
// full context through, so a pound row floors on the athlete's OWN lb
// inventory, same as a kg row does on kg.

export type LoadableContext = {
  /** Finest loadable increment in kg, for kg-unit rows. Derived from the
   *  athlete's configured kg plate inventory (`loadRoundingContext`, which
   *  feeds `smallestLoadableStep`); omit to fall back to
   *  DEFAULT_LOADABLE_STEP_KG (the un-configured default). */
  stepKg?: number;
  /** Finest loadable increment in lb, for lb-unit rows — a step read off the
   *  athlete's **lb** inventory, in its own unit, never the kg one converted.
   *  Same source as `stepKg` (`loadRoundingContext`), just the other unit's
   *  configured inventory; omit to fall back to DEFAULT_LOADABLE_STEP_LB. */
  stepLb?: number;
  /** CN-39 — the same two steps for iron that goes on ONE end (landmine,
   *  T-bar, sled, per-arm lever). A 1.25 plate moves a symmetric bar by 2.5
   *  and a landmine by 1.25, so "the finest rung this athlete can build" has
   *  TWO answers per unit, not one.
   *
   *  ⛔ They live in the same context rather than in a second one for the
   *  reason this file's header already gives about the two units: a context
   *  built in two places is a context that can be built HALF. Omit them and
   *  the symmetric steps are used, which is what every caller did before. */
  singleStepKg?: number;
  singleStepLb?: number;
};

const stepFor = (
  unit: "kg" | "lb",
  ctx: LoadableContext,
  mode: PlateLoadMode,
): number => {
  if (mode === "single") {
    // ⛔ Fall back to the symmetric step, not to the constant: an athlete with
    // a configured inventory and no single-side entry is better served by
    // their own iron, coarsely, than by a default that is not theirs at all.
    const single = unit === "kg" ? ctx.singleStepKg : ctx.singleStepLb;
    if (single !== undefined) return single;
  }
  return unit === "kg"
    ? ctx.stepKg ?? DEFAULT_LOADABLE_STEP_KG
    : ctx.stepLb ?? DEFAULT_LOADABLE_STEP_LB;
};

/**
 * The next loadable rung ABOVE a weight — the mirror of `roundLoadable`, which
 * floors. Lives here rather than in the caller because "what weights can this
 * athlete actually build" is one concept and it already owns it: a second
 * module picking its own step is how two ideas of a loadable weight start to
 * disagree.
 *
 * Used by the set proposal (EQ-2 §7.4): "more load at the same reps" has to
 * land on a number the athlete can put on the bar, and erring UP by one rung
 * is correct here — the whole point is to ask for slightly more. `rungs`
 * climbs further for the trade option (heavier load, fewer reps).
 *
 * Returns canonical kg. A non-finite or non-positive input passes through
 * untouched, same contract as `roundLoadable`.
 */
export const nextLoadableUp = (
  weightKg: number,
  unit: "kg" | "lb",
  ctx: LoadableContext = {},
  rungs = 1,
  mode: PlateLoadMode = "pairs",
): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return weightKg;
  const step = stepFor(unit, ctx, mode);
  if (!Number.isFinite(step) || step <= 0 || rungs < 1) return weightKg;
  const display = fromCanonicalKg(weightKg, unit);
  // Mirror of roundLoadable's epsilon: a weight already exactly on a rung
  // must climb a full rung, not be nudged by float error into staying put.
  const current = Math.floor(display / step + 1e-6);
  return toCanonicalKg((current + rungs) * step, unit);
};

/**
 * The loadable rung NEAREST a weight — the third of the family, beside
 * `roundLoadable` (floors) and `nextLoadableUp` (climbs).
 *
 * ⛔ It exists because the other two answer a different question. Those two
 * serve PRESCRIPTION, where the direction is the point: never ask for more
 * than planned, or deliberately ask for one rung more. This one serves a
 * GESTURE — the athlete taps «≈» on a number they are looking at and means
 * "give me the closest weight I can actually build". Erring downward there
 * would be a silent 2.5 kg the athlete did not ask to lose.
 * (Antenore, 2026-09-11: «al più vicino e al netto della barra».)
 *
 * ⚠️ "Al netto della barra" is the CALLER's job, not this function's: it knows
 * nothing about bars, and it must not — a curl bar of 7 with a 2.5 step makes
 * every rung of the TOTAL unloadable, so the caller subtracts the bar, snaps
 * what goes on it, and adds the bar back. Getting that wrong makes the button
 * propose weights that cannot be built, which is the exact opposite of what it
 * promises.
 *
 * Ties go UP: at an exact half-step the athlete asked for more, not less.
 * Returns canonical kg; a non-finite or non-positive input passes through.
 */
export const nearestLoadable = (
  weightKg: number,
  unit: "kg" | "lb",
  ctx: LoadableContext = {},
  mode: PlateLoadMode = "pairs",
): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return weightKg;
  const step = stepFor(unit, ctx, mode);
  if (!Number.isFinite(step) || step <= 0) return weightKg;
  const display = fromCanonicalKg(weightKg, unit);
  // Same epsilon as the siblings: a weight already sitting on a rung must not
  // be nudged off it by float error.
  const rungs = Math.round(display / step + 1e-6);
  // ⛔ Never wipe a light load to an empty bar — the sub-step case `roundLoadable`
  // guards too. Here it means the first rung, not the original: the athlete
  // asked to land on something loadable, and zero is not.
  if (rungs < 1) return toCanonicalKg(step, unit);
  return toCanonicalKg(rungs * step, unit);
};

export const roundLoadable = (
  weightKg: number,
  unit: "kg" | "lb",
  ctx: LoadableContext = {},
  /** CN-39 — the loading shape of THIS exercise. Omitted means symmetric,
   *  which is what every caller meant before the parameter existed. */
  mode: PlateLoadMode = "pairs",
): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return weightKg;
  const step = stepFor(unit, ctx, mode);
  if (!Number.isFinite(step) || step <= 0) return weightKg;

  const display = fromCanonicalKg(weightKg, unit);
  // +epsilon so a weight already sitting exactly on a step (e.g. 67.5 with
  // step 2.5 → 27.0) isn't dropped a whole rung by IEEE float error.
  const rungs = Math.floor(display / step + 1e-6);
  const floored = rungs * step;
  // A sub-step target (target < one increment) floors to 0 — that would wipe
  // a light prescription to an empty bar. Keep the original rather than show
  // 0; a finer increment for light work is a Layer-1 equipment concern.
  if (floored <= 0) return weightKg;
  return toCanonicalKg(floored, unit);
};
